import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Header } from '../components/Header';
import { Input } from '../components/ui/input';
import { getActivityLogs, getDeals } from '../lib/api';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Search, Shield, Activity, FileText, Phone, User, X, Clock } from 'lucide-react';

export const ComplianceLogPage = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [deals, setDeals] = useState([]);

  useEffect(() => {
    loadData();
  }, [categoryFilter]);

  const loadData = async () => {
    try {
      const params = { limit: 200 };
      if (categoryFilter) {
        params.category = categoryFilter;
      }
      
      const [activitiesRes, dealsRes] = await Promise.all([
        getActivityLogs(params),
        getDeals({}),
      ]);
      
      setActivities(activitiesRes.data);
      setDeals(dealsRes.data);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const filteredActivities = activities.filter(a =>
    a.action.toLowerCase().includes(search.toLowerCase()) ||
    a.details.toLowerCase().includes(search.toLowerCase()) ||
    a.user_name.toLowerCase().includes(search.toLowerCase())
  );

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'stip':
        return <FileText size={14} className="text-blue-600" />;
      case 'interview':
        return <Phone size={14} className="text-amber-600" />;
      case 'ai':
        return <Activity size={14} className="text-purple-600" />;
      case 'auth':
        return <User size={14} className="text-emerald-600" />;
      default:
        return <Shield size={14} className="text-slate-600" />;
    }
  };

  const getCategoryStyle = (category) => {
    switch (category) {
      case 'stip':
        return 'bg-blue-100 text-blue-700';
      case 'interview':
        return 'bg-amber-100 text-amber-700';
      case 'ai':
        return 'bg-purple-100 text-purple-700';
      case 'auth':
        return 'bg-emerald-100 text-emerald-700';
      case 'deal':
        return 'bg-sky-100 text-sky-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  // Compliance Stats
  const complianceStats = {
    totalDeals: deals.length,
    greenCompliance: deals.filter(d => d.compliance_status === 'green').length,
    yellowCompliance: deals.filter(d => d.compliance_status === 'yellow').length,
    redCompliance: deals.filter(d => d.compliance_status === 'red').length,
    avgComplianceScore: deals.length > 0
      ? Math.round(deals.reduce((acc, d) => acc + d.compliance_score, 0) / deals.length)
      : 0,
  };

  return (
    <Layout>
      <Header
        title="Compliance & Activity Log"
        subtitle="Track all compliance and staff actions"
        onRefresh={loadData}
      />

      <div className="p-6 space-y-6" data-testid="compliance-log">
        {/* Compliance Overview */}
        <div className="bg-white border border-slate-200 p-6">
          <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-slate-900 mb-4">
            Compliance Overview
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center p-4 bg-slate-50 border border-slate-200">
              <p className="text-3xl font-mono font-bold text-slate-900">{complianceStats.totalDeals}</p>
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mt-1">Total Deals</p>
            </div>
            <div className="text-center p-4 bg-emerald-50 border border-emerald-200">
              <p className="text-3xl font-mono font-bold text-emerald-700">{complianceStats.greenCompliance}</p>
              <p className="text-xs uppercase tracking-wider text-emerald-600 font-semibold mt-1">Compliant</p>
            </div>
            <div className="text-center p-4 bg-amber-50 border border-amber-200">
              <p className="text-3xl font-mono font-bold text-amber-700">{complianceStats.yellowCompliance}</p>
              <p className="text-xs uppercase tracking-wider text-amber-600 font-semibold mt-1">Warning</p>
            </div>
            <div className="text-center p-4 bg-red-50 border border-red-200">
              <p className="text-3xl font-mono font-bold text-red-700">{complianceStats.redCompliance}</p>
              <p className="text-xs uppercase tracking-wider text-red-600 font-semibold mt-1">Critical</p>
            </div>
            <div className="text-center p-4 bg-slate-50 border border-slate-200">
              <p className="text-3xl font-mono font-bold text-slate-900">{complianceStats.avgComplianceScore}</p>
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mt-1">Avg Score</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border border-slate-200 p-4 flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search activities..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-activities"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X size={16} />
              </button>
            )}
          </div>

          <Select value={categoryFilter || "all"} onValueChange={(v) => setCategoryFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-40" data-testid="filter-category">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="deal">Deal</SelectItem>
              <SelectItem value="stip">Stipulation</SelectItem>
              <SelectItem value="interview">Interview</SelectItem>
              <SelectItem value="ai">AI</SelectItem>
              <SelectItem value="auth">Auth</SelectItem>
              <SelectItem value="settings">Settings</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Activity Feed */}
        <div className="bg-white border border-slate-200">
          <div className="p-4 border-b border-slate-100">
            <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-slate-900">
              Activity Feed
            </h3>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <p className="text-slate-500">Loading activities...</p>
            </div>
          ) : filteredActivities.length === 0 ? (
            <div className="p-8 text-center">
              <Activity size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No activities found</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
              {filteredActivities.map((activity, idx) => (
                <div
                  key={idx}
                  className="p-4 hover:bg-slate-50 transition-colors"
                  data-testid={`activity-${idx}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-8 h-8 flex items-center justify-center ${getCategoryStyle(activity.category)}`}>
                      {getCategoryIcon(activity.category)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-2 py-0.5 text-xs font-semibold uppercase ${getCategoryStyle(activity.category)}`}>
                          {activity.category}
                        </span>
                        <span className="text-sm font-medium text-slate-900">
                          {activity.action.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 mt-1">{activity.details}</p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                        <span className="flex items-center gap-1">
                          <User size={12} />
                          {activity.user_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={12} />
                          {new Date(activity.timestamp).toLocaleString()}
                        </span>
                        {activity.deal_id && (
                          <span className="font-mono">Deal: {activity.deal_id.slice(0, 8)}...</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};
